/*
 * The start screen (owner, 13 Oct 2026: "some assets are half loaded and looks broken for a sec… a logo loading
 * screen that covers and hides this"). index.html paints the logo in the first frame, before any script runs. The app
 * lifts it once the first screen is complete: every database read, picture and photo asked for while it was up has
 * answered, with two frames in a row asking nothing new, the fonts are in and the pictures on screen are decoded.
 * Before, a start showed the app in the fallback font with empty widgets, then the font swapped and Home jumped into
 * place as its parts arrived (~100 ms on this PC, longer on the owner's Mac). Never held longer than MAX_MS after
 * the app or sign-in screen first drew; lifted at once in a hidden tab and on the "open in another tab" and
 * "damaged" screens.
 */

const MAX_MS = 2000;
let pending = 0;
let state: "up" | "waiting" | "lifted" = "up";
let liftedNow = () => {};
/** Settles once the start screen is gone: work that can wait (the phone's editor warm-up) starts after it. */
export const lifted = new Promise<void>((r) => (liftedNow = r));

/** `p`, counted as part of the first screen while the start screen is up. */
export function booting<T>(p: Promise<T>): Promise<T> {
  if (state === "lifted") return p;
  pending++;
  const done = () => void pending--;
  p.then(done, done);
  return p;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
// rAF stops in a background tab; the timer keeps each wait short.
const frame = () =>
  new Promise<void>((r) => {
    requestAnimationFrame(() => r());
    setTimeout(r, 100);
  });

function onScreen(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.bottom > 0 && r.right > 0 && r.top < innerHeight && r.left < innerWidth;
}

/** Called when the app or the sign-in screen has drawn: lifts the start screen once that screen is complete. */
export function liftWhenReady(): void {
  if (state !== "up") return;
  state = "waiting";
  performance.mark("engram:drawn");
  const deadline = performance.now() + MAX_MS;
  const left = () => Math.max(0, deadline - performance.now());
  const capped = (p: Promise<unknown>) => Promise.race([p.catch(() => undefined), sleep(left())]);
  void (async () => {
    // The UI font starts loading now rather than at the first layout that uses it.
    const font = capped(document.fonts.load("1em 'Inter Variable'"));
    for (let quiet = 0; quiet < 2 && left() > 0 && !document.hidden; ) {
      await frame();
      quiet = pending === 0 ? quiet + 1 : 0;
    }
    if (!document.hidden) {
      await font;
      await capped(document.fonts.ready);
      const pictures = [...document.querySelectorAll<HTMLImageElement>("#root img")].filter(onScreen);
      await capped(Promise.all(pictures.map((img) => img.decode().catch(() => undefined))));
    }
    lift();
  })();
}

/** Takes the start screen away (a short fade; none in a hidden tab). */
export function lift(): void {
  if (state === "lifted") return;
  state = "lifted";
  performance.mark("engram:lifted");
  liftedNow();
  const el = document.getElementById("boot");
  if (!el) return;
  if (document.hidden) return el.remove();
  el.classList.add("lifting");
  el.addEventListener("transitionend", () => el.remove(), { once: true });
  setTimeout(() => el.remove(), 400);
}
